import gridDateFormat from 'constants/dateFormats';
import { isDate, isNull } from 'lodash';
import { timeOptionShort } from './staticOptions';

// find all the unique fields for a nested array by key name
const uniqueFields = (array: Array<{ [key: string]: any }>, key: string): string[] => {
  const budget = array.map((el) => el[key]);

  const uniqueKeys = (data: any[] = []): string[] => {
    const unique = Object.keys(
      data.reduce((result, obj) => {
        return Object.assign(result, obj);
      }, {})
    );

    return unique;
  };

  const result = uniqueKeys(budget);

  return result;
};

export function getPeak(startDate: Date) {
  return startDate?.getDay?.() > 4 || null;
}

export function onlyUnique(value = '', index: number, self: string[]) {
  return self?.indexOf(value) === index;
}

export function uniqueFromArray(myArray: any[]) {
  return myArray
    .filter((value, index, array) => array.indexOf(value) === index && value)
    .filter((el) => el !== undefined);
}

export function capitalizeFirstLetter(string = '') {
  return string?.charAt(0)?.toUpperCase() + string?.slice(1) || '';
}

export function formatPhoneNumber(phoneNumberString: string) {
  const cleaned = ('' + phoneNumberString).replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return '(' + match[1] + ') ' + match[2] + '-' + match[3];
  }
  return null;
}

export function removeCSSProperties(html = '', properties: string[] = []) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const elements = doc.getElementsByTagName('*');

  properties.forEach((property) => {
    [...elements].forEach((element) => (element as HTMLElement).style.removeProperty(property));
  });

  // Return only the body's innerHTML to avoid XML wrapper and preserve literal special characters
  return doc.body.innerHTML;
}

// Decode HTML entities to literal characters (e.g., &amp; -> &, &lt; -> <)
export function decodeHtmlEntities(value?: string | null): string {
  if (!value) {
    return '';
  }

  if (typeof window === 'undefined') {
    return value;
  }

  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
}

export function convertCamelCaseToWords(camelCaseStr: string) {
  // Split the string by capital letters
  var words = camelCaseStr.split(/(?=[A-Z])/);

  // Capitalize each word
  var capitalizedWords = words.map(function (word) {
    return word.charAt(0).toUpperCase() + word.slice(1);
  });

  // Join the words with a space
  var result = capitalizedWords.join(' ');

  return result;
}

export function addArrayValues(arr: any[], property: string) {
  return arr.reduce((acc, obj) => {
    if (!obj) return acc;
    return +acc + +(obj[property] || 0);
  }, 0);
}

// Sum internal cost items into an EXTENDED (all-units) total. By default an item
// scales with qty (a per-unit cost, e.g. a per-musician fee). An item flagged
// `perUnit === false` is FLAT — a one-time cost counted once regardless of qty
// (e.g. a setup fee or single rental), mirroring how the talent roster is a
// fixed total. Flagless legacy items are treated as per-unit (perUnit !== false).
function internalItemsExtended(
  items: { amount?: number; perUnit?: boolean }[],
  qty: number
) {
  return items.reduce((acc, c) => {
    const amount = Number(c?.amount) || 0;
    return acc + (c?.perUnit === false ? amount : qty * amount);
  }, 0);
}

// EXTENDED (all-units) actual internal cost: actual-flagged items, each scaled by
// qty unless flat. An item counts as actual unless explicitly opted out
// (inActual === false), so flagless legacy items stay counted.
// A service's always-set display title (heading, or the underlying service name).
export function getServiceTitle(service: { heading?: string; service?: string }) {
  return service.heading || service.service || 'Service';
}

// Preferred label for a service: nickname wins when set, otherwise the title.
export function getServiceDisplayLabel(service: {
  heading?: string;
  nickname?: string;
  service?: string;
}) {
  return service.nickname || getServiceTitle(service);
}

export function serviceInternalActualExtended(service: {
  qty?: number;
  internalCosts?: { amount: number; inActual?: boolean; perUnit?: boolean }[] | null;
}) {
  const items = (service.internalCosts || []).filter((c) => c?.inActual !== false);
  return internalItemsExtended(items, service.qty ?? 1);
}

// EXTENDED (all-units) estimate internal cost: estimate-flagged items (inEstimate
// truthy), each scaled by qty unless flat.
export function serviceInternalEstimateExtended(service: {
  qty?: number;
  internalCosts?: { amount: number; inEstimate?: boolean; perUnit?: boolean }[] | null;
}) {
  const items = (service.internalCosts || []).filter((c) => c?.inEstimate);
  return internalItemsExtended(items, service.qty ?? 1);
}

// Effective per-unit talent estimate. A manual override (talentEstimateOverride)
// wins when present (including an explicit 0); otherwise fall back to the frozen
// template snapshot (talentEstimate). null/undefined override === no override.
export function effectiveTalentEstimate(service: {
  talentEstimate?: number;
  talentEstimateOverride?: number | null;
}) {
  return service.talentEstimateOverride ?? service.talentEstimate ?? 0;
}

// Extended (total) estimate = qty × per-unit talent estimate (override or template
// snapshot) + estimate internal items (scaled unless flat).
export function serviceEstimateExtended(service: {
  qty?: number;
  talentEstimate?: number;
  talentEstimateOverride?: number | null;
  internalCosts?: { amount: number; inEstimate?: boolean; perUnit?: boolean }[] | null;
}) {
  return (service.qty ?? 1) * effectiveTalentEstimate(service) + serviceInternalEstimateExtended(service);
}

// PER-UNIT "Int Est" (grid column) = extended ÷ qty, so it lines up with the
// per-unit External price. Flat items contribute amount/qty here (their share of
// one unit), exactly like the roster does for Int Actual.
export function serviceEstimateTotal(service: {
  qty?: number;
  talentEstimate?: number;
  internalCosts?: { amount: number; inEstimate?: boolean; perUnit?: boolean }[] | null;
}) {
  const qty = service.qty ?? 1;
  return qty > 0 ? serviceEstimateExtended(service) / qty : serviceEstimateExtended(service);
}

// --- Gig <-> service linkage -------------------------------------------------
// A gig can bill across several services. `relServiceIds` holds them in order; the FIRST is the
// default billing service. Which pay line goes where is per rate line (Rate.serviceId), set from
// the rate/service matrix on the Talent grid's Services cell.

type GigServiceLink = { relServiceIds?: string[] };
type RateLike = { amount?: number; type?: string; serviceId?: string };

// Every service this gig is linked to. Tolerates a missing field; never returns undefined.
export function getGigServiceIds(gig: GigServiceLink): string[] {
  return gig?.relServiceIds ?? [];
}

// The default billing service — the first link. '' when the gig is unlinked.
export function getGigPrimaryServiceId(gig: GigServiceLink): string {
  return getGigServiceIds(gig)[0] ?? '';
}

// Which service one rate line bills to: its own serviceId when the gig is still linked to that
// service, otherwise the first link. Removing a service normally CLEARS the stored id at write time
// (see pruneRateServiceIds) — the validity check here is the defensive net for legacy or racy docs,
// so a stale id can never strand a rate or resurrect an old assignment unseen.
export function getRateServiceId(gig: GigServiceLink, rate: RateLike): string {
  const ids = getGigServiceIds(gig);
  return rate?.serviceId && ids.includes(rate.serviceId) ? rate.serviceId : ids[0] ?? '';
}

// Total pay on this gig attributed to one service, summed per rate line.
export function gigRateTotalForService(
  gig: GigServiceLink & { rates?: RateLike[] },
  serviceId: string
): number {
  if (!serviceId) return 0;
  return (gig?.rates || []).reduce(
    (sum, rate) => (getRateServiceId(gig, rate) === serviceId ? sum + (Number(rate.amount) || 0) : sum),
    0
  );
}

// Rewrite rate lines whose serviceId is no longer among the gig's services back to unassigned, so
// storage matches what the rollups show and re-adding that service later cannot silently reclaim
// the line. Writes '' rather than deleting the key: sanitizeDiff turns undefined into '' anyway.
export function pruneRateServiceIds<T extends RateLike>(ids: string[], rates: T[] = []): T[] {
  return rates.map((rate) =>
    rate?.serviceId && !ids.includes(rate.serviceId) ? { ...rate, serviceId: '' } : rate
  );
}

// Build a map of serviceId -> total talent (gig rate) cost, fanned out PER RATE LINE so a gig
// spanning two services contributes each line to exactly one of them. For gigs with at least one
// service link the map therefore sums to those gigs' rate total — it can never double count.
// Unlinked gigs contribute nothing (as before), so this does NOT equal the financial breakdown's
// gigsTotal, which counts every visible gig. Display-only: these costs are still counted once in
// gigsTotal, so this map must not be added into any breakdown total.
export function buildServiceTalentMap(
  gigs: (GigServiceLink & { rates?: RateLike[] })[] = []
): Map<string, number> {
  const map = new Map<string, number>();
  gigs.forEach((gig) => {
    // Real emptiness check: an empty array is truthy, so `!gig.relServiceIds` would not do.
    if (getGigServiceIds(gig).length === 0) return;
    (gig.rates || []).forEach((rate) => {
      const target = getRateServiceId(gig, rate);
      if (!target) return;
      map.set(target, (map.get(target) ?? 0) + (Number(rate.amount) || 0));
    });
  });
  return map;
}

// EXTENDED (total) actual cost for all units = real roster talent (counted ONCE)
// + actual internal items (scaled by qty unless flat). This mirrors the
// financial breakdown (gigs once via gigsTotal, items via the same helper) and
// is the cost basis the Margin compares against qty-scaled revenue.
export function serviceActualExtended(
  service: {
    id?: string;
    qty?: number;
    internalCosts?: { amount: number; inActual?: boolean; perUnit?: boolean }[] | null;
  },
  talentMap: Map<string, number>
) {
  const roster = service.id ? talentMap.get(service.id) ?? 0 : 0;
  return roster + serviceInternalActualExtended(service);
}

// PER-UNIT "Int Actual" (grid column) = extended ÷ qty, so it is directly
// comparable to the per-unit External price. The roster and any flat items are
// fixed totals, so they divide down to a per-unit share; per-unit items pass
// through at full value.
export function serviceActualTotal(
  service: {
    id?: string;
    qty?: number;
    internalCosts?: { amount: number; inActual?: boolean; perUnit?: boolean }[] | null;
  },
  talentMap: Map<string, number>
) {
  const qty = service.qty ?? 1;
  return qty > 0
    ? serviceActualExtended(service, talentMap) / qty
    : serviceActualExtended(service, talentMap);
}

export function addArrrayValuesCondition(arr = [], property: string, check: string) {
  return arr?.reduce?.((acc, obj) => {
    return acc + (obj[check] ? obj[property] : 0);
  }, 0);
}

export function averageArrayValues(arr = []) {
  const avg = arr.reduce((p, c, i) => {
    return p + (c - p) / (i + 1);
  }, 0);

  return avg;
}

export function addFlatArray(arr = []) {
  const sum = arr?.reduce?.((pv, cv) => {
    return typeof cv === 'number' ? pv + cv : 0;
  }, 0);
  return sum;
}

export function numberWithCommas(x: string | number, zeroToDash = true) {
  const parts = x.toString().split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  if (zeroToDash && parts[0] === '0') {
    return '-';
  }
  return parts.join('.');
}

// for INCOMING raw dates
export const offset = (utcTime: Date) => {
  const tzoffset = utcTime.getTimezoneOffset() * 60000;
  const localTime = new Date(utcTime.getTime() + tzoffset);
  return localTime;
};

// for OUTGOING raw dates
export const reverseOffset = (utcTime: Date | null) => {
  if (!utcTime || isNaN(utcTime?.getTime?.())) {
    return null;
  }
  const tzoffset = utcTime?.getTimezoneOffset() * 60000;
  const localTime = new Date(utcTime?.getTime?.() - tzoffset);
  return localTime;
};

// Bookkeeping-instant keys that must NOT be reverse-offset on write. This set is the exact mirror
// of the no-offset whitelist in useProcessTimeStamps (the read hook): every event-timing Date is
// offset on read and reverse-offset on write; these are real instants, left untouched on both sides.
// Keep the two lists identical.
const EVENT_TIME_EXEMPT_KEYS = new Set(['timestamp', 'finalizedAt', 'modified']);

/**
 * Deep-applies `reverseOffset` to every event-timing Date in a value (arrays/objects included),
 * skipping Dates under the bookkeeping keys above. Use for write paths that bypass `sanitizeDiff`
 * (segment rows, flex timeline items, client portal responses) so their wall-clock times are stored
 * as wall-clock-as-UTC, matching how event `timeStart`/`timeEnd` are already saved.
 */
export const reverseOffsetEventTimes = <T>(value: T, key?: string): T => {
  if (value instanceof Date) {
    return (key && EVENT_TIME_EXEMPT_KEYS.has(key) ? value : reverseOffset(value)) as unknown as T;
  }
  if (Array.isArray(value)) {
    return value.map((v) => reverseOffsetEventTimes(v)) as unknown as T;
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const k in value) {
      if (Object.prototype.hasOwnProperty.call(value, k)) {
        out[k] = reverseOffsetEventTimes((value as Record<string, unknown>)[k], k);
      }
    }
    return out as unknown as T;
  }
  return value;
};

// for setting a range against event queries (IN UTC) or timestamps (IN LOCAL TIME).
// assumes the picker is set in UTC
export const convertDateRange = (dates: (Date | null)[], dateQuery: string) => {
  if (!dates[0] || !dates[1]) return null;

  let newDates = dates.map((date) => new Date(date!.getTime())); // Clone dates to avoid mutating the original array

  if (dateQuery === 'dateStart') {
    // Set start of the day in UTC
    newDates[0].setUTCHours(0, 0, 0, 0); // Midnight UTC

    // Set end of the day in UTC
    newDates[1].setUTCHours(23, 59, 59, 999); // End of day UTC
  } else {
    // Set start of the day in local time
    newDates[0].setHours(0, 0, 0, 0); // Midnight local time

    // Set end of the day in local time
    newDates[1].setHours(23, 59, 59, 999); // End of day local time
  }

  return newDates;
};

export interface RoleCategory {
  id: string;
  children: Record<string, RoleCategory>;
}

export function getTopParentCategory(
  obj: Record<string, RoleCategory>,
  id: string
): (string | null)[] {
  let topLevel: string | null = null;
  let twoLevelsUp: string | null = null;
  let oneLevelUp: string | null = null;

  function findCategory(
    currentObj: Record<string, RoleCategory>,
    currentId: string,
    parentKey: string | null,
    grandParentKey: string | null
  ): boolean {
    for (let key in currentObj) {
      if (currentObj[key].id === currentId) {
        oneLevelUp = parentKey;
        twoLevelsUp = grandParentKey;
        return true;
      }
      if (findCategory(currentObj[key].children, currentId, key, parentKey)) {
        return true;
      }
    }
    return false;
  }

  for (let key in obj) {
    if (obj[key].id === id || findCategory(obj[key].children, id, key, null)) {
      topLevel = key;
      break;
    }
  }

  return [topLevel, twoLevelsUp, oneLevelUp];
}

export function getTimeAgo(date: Date) {
  const now = new Date();
  const secondsPast = (now.getTime() - date.getTime()) / 1000;

  if (secondsPast < 60) {
    return 'Just now';
  }
  if (secondsPast < 300) {
    return `${Math.round(secondsPast / 60)} minutes ago`;
  }
  if (secondsPast >= 300) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return '';
}

export function goToLink(link: string) {
  if (!link) return;
  if (!link.startsWith('http://') && !link.startsWith('https://')) {
    link = 'https://' + link;
  }
  window.open(link, '_blank');
}

export function simpleDateTimeString(time: Date | null | undefined) {
  if (!time) return '';
  const dateString = time.toLocaleDateString([], gridDateFormat) || '';
  const timeString = time.toLocaleTimeString([], timeOptionShort) || '';
  return `${dateString} ${timeString}`;
}

export function hexToRGBA(hex: string, opacity: number) {
  let r = parseInt(hex.slice(1, 3), 16),
    g = parseInt(hex.slice(3, 5), 16),
    b = parseInt(hex.slice(5, 7), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

export function getSubdomain(url: string) {
  var host = new URL(url).hostname;
  var parts = host.split('.');
  if (parts.length > 2) {
    return parts[0];
  }
  return null;
}

export function isPrime(date: Date | null): boolean {
  if (!date || !isDate(date) || isNull(date)) {
    return false;
  }
  const day = date.getDay();
  return day === 6;
}

export function twoWeekWarning(dateStart: Date | null) {
  if (!dateStart) return false;
  const today = new Date();
  const startDate = new Date(dateStart);
  const twoWeeksLater = new Date(today);
  twoWeeksLater.setDate(today.getDate() + 14);
  return startDate >= today && startDate <= twoWeeksLater;
}

// Helper function to normalize date ranges for queries
export const normalizeDateForQuery = (date: Date | null, isEndDate: boolean): Date | null => {
  if (!date) return null;

  const normalizedDate = new Date(date);
  if (isEndDate) {
    normalizedDate.setHours(23, 59, 59, 999);
  } else {
    normalizedDate.setHours(0, 0, 0, 0);
  }
  return normalizedDate;
};

export const formatNames = (names: string[]): string => {
  if (!names || names.length === 0) {
    return '';
  }
  if (names.length === 1) {
    return names[0];
  }
  if (names.length === 2) {
    return `${names[0]} and ${names[1]}`;
  }
  // For more than two names, join all but the last with a comma, then add "and" before the last name.
  const allButLast = names.slice(0, -1).join(', ');
  const last = names[names.length - 1];
  return `${allButLast} and ${last}`;
};

export default uniqueFields;
